import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AsyncPipe, NgClass, NgComponentOutlet, NgStyle, NgTemplateOutlet } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SafeSvgPipe } from '@shared/pipes';
import * as SvgResources from '../../../../lib/svg.resources';
import { Observable } from 'rxjs';
import { debounceTime, filter, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { FacetedFilterPanelSearch } from '../../../../services/faceted-filter-panel-search.service';
import { FilterPanelValueService } from '../../../../services/filter-panel-value.service';
import { FilterPanelRef, FilterPanelTypes } from '../../../../types';
import { FilterPanelCloseType, FilterPanelDisplayContext } from '../../../../types/constants';
import { IFacetedFilterDataItem, IFacetedFilterSelection } from '../../../../types/faceted-filter.types';
import { PaneInstanceManager, PaneInstanceScrollDirection } from '../../../../Utils/pane-instance.manager';
import { FilterPanelHostComponent } from '../../default/filter-panel-host.component';

@Component({
  templateUrl: './faceted-filter-panel-host.component.html',
  styleUrls: ['./faceted-filter-panel-host.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    NgComponentOutlet,
    NgStyle,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    SafeSvgPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacetedFilterPanelHostComponent extends FilterPanelHostComponent implements OnInit, OnDestroy {
  private _panelWidth = 230;
  filterDataItems: IFacetedFilterDataItem[] = [];
  // filterDataInjector: Injector;

  searchField = new FormControl();
  mobileSearchEnabled = false;
  displayContext!: FilterPanelDisplayContext;

  panelTitle = '';

  facetedFilterApplyButtonDisabled = true;

  backIcon = SvgResources.sizableSvgIcon(SvgResources.svgIconTypes.chevronDblLeft, 6);
  searchIcon = SvgResources.sizableSvgIcon(SvgResources.svgIconTypes.searchSvg, 6);
  chevronIcon = SvgResources.sizableSvgIcon(SvgResources.svgIconTypes.chevronRight, 4);

  scrollDirection = PaneInstanceScrollDirection;

  activeLocations$!: Observable<ComponentRef<unknown>[]>;

  @ViewChild('rootContent') rootContent!: ElementRef;

  constructor(
    protected filterPanelRef: FilterPanelRef,
    protected valueService: FilterPanelValueService,
    private parentInjector: Injector,
    private searchService: FacetedFilterPanelSearch
  ) {
    super(filterPanelRef, valueService);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.activeLocations$ = PaneInstanceManager.activePaneLocations$;

    this.filterPanelRef.config.inputData.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.filterDataItems = [...(data.inputData as IFacetedFilterDataItem[])];
    });

    this._subscription.add(
      this.valueService.facetedFilterApplyDisabled$.subscribe((isDisabled) => {
        this.facetedFilterApplyButtonDisabled = isDisabled;
      })
    );

    this.searchService.searchState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state.searchCleared) this.searchField.patchValue(null);
      this.mobileSearchEnabled = state.isSearching;
    });

    this.searchField.valueChanges
      .pipe(
        debounceTime(500),
        filter((term) => term !== null),
        takeUntil(this.destroy$),
        // distinctUntilChanged(),
        startWith(''),
        switchMap((term) => this.searchService.searchData(this.filterDataItems, term))
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  startMobileSearch(): void {
    this.searchService.startMobileSearch();
    this.mobileSearchEnabled = true;
  }

  clearSearch(): void {
    this.searchField.patchValue('');
    this.searchService.clearSearch();
  }

  getBreadcrumbLabel(item: ComponentRef<unknown>): string {
    const instance = item.instance as {
      replaying?: boolean;
      replayItem?: { item?: { label?: string } };
      item?: { label?: string };
    };
    return instance.replaying === true && instance.replayItem?.item
      ? (instance.replayItem.item.label ?? '')
      : (instance.item?.label ?? '');
  }

  closeFacetedFilterPanel(): void {
    this.valueService.clearSelectionInfo();
    this.searchService.clearSearch();
    const result: FilterPanelTypes.IFilterPanelResult = {
      type: FilterPanelCloseType.cancel,
      data: [],
      resultType: FilterPanelCloseType.cancel,
      category: '',
      target: '',
    };
    PaneInstanceManager.purgeActiveInstances();
    this.context.close(result);
  }

  facetedFilterApplySelected(): void {
    const selection: IFacetedFilterSelection = this.valueService.getSelectionResult();
    this.valueService.clearSelectionInfo();
    this.searchService.clearSearch();
    const result: FilterPanelTypes.IFilterPanelResult = {
      type: FilterPanelCloseType.applied,
      data: [selection],
      resultType: FilterPanelCloseType.applied,
      category: '',
      target: '',
    };
    PaneInstanceManager.purgeActiveInstances();
    this.context.close(result);
  }

  onBreadcrumbItemClick(item: ComponentRef<unknown>): void {
    item.location.nativeElement.previousElementSibling.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start',
    });
  }

  onPaneNavigationClick(direction: PaneInstanceScrollDirection): void {
    PaneInstanceManager.mobileScrollTo(direction, this.rootContent);
  }

  // private createFacetedFilterInjector(): void {
  // 	this.filterDataInjector = Injector.create({
  // 		providers: [{ provide: FACETED_FILTER_DATA_TOKEN, useValue: this.filterData }],
  // 		parent: this.parentInjector
  // 	});
  // }
}
